import React, { useState, useEffect, useCallback } from 'react';
import { syncService } from '../../application/services/SyncService';
import type { MarkupSettings, SyncLog, SyncStatus } from '../../infrastructure/repositories/SyncRepository';

type Tab = 'settings' | 'log';
const PAGE_SIZE = 10;
const POLL_INTERVAL = 2000;

const PHASE_LABELS: Record<string, string> = {
  catalog: 'Каталог (со склада)',
  engine: 'Engine (из-за границы)',
  deferred: 'Повторная обработка',
  refresh: 'Обновление метаданных',
  idle: 'Завершено',
};

export const SyncMarkupPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('settings');
  const [settings, setSettings] = useState<MarkupSettings | null>(null);
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [logPage, setLogPage] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [s, st, l] = await Promise.all([
        syncService.getSettings(),
        syncService.getStatus(),
        syncService.getLogs(),
      ]);
      setSettings(s);
      setStatus(st);
      setLogs(l);
    } catch (err: any) {
      setError(err?.message || 'Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Поллинг статуса пока идёт синхронизация
  useEffect(() => {
    if (!status?.isRunning) return;
    const interval = setInterval(async () => {
      try {
        const st = await syncService.getStatus();
        setStatus(st);
        if (!st.isRunning) {
          // Синхронизация завершилась — обновляем логи
          const l = await syncService.getLogs();
          setLogs(l);
        }
      } catch {}
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [status?.isRunning]);

  const handleSave = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await syncService.updateSettings(settings);
      setSuccess('Настройки сохранены');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerSync = async () => {
    try {
      setError(null);
      await syncService.triggerSync();
      setSuccess('Синхронизация запущена');
      setTimeout(() => { setSuccess(null); loadData(); }, 2000);
    } catch (err: any) {
      setError(err?.message || 'Не удалось запустить синхронизацию');
    }
  };

  const handleCancelSync = async () => {
    try {
      await syncService.cancelSync();
      setSuccess('Отмена запрошена');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.message || 'Не удалось отменить');
    }
  };

  const handleRefreshProducts = async () => {
    try {
      setError(null);
      await syncService.refreshProducts();
      setSuccess('Обновление метаданных запущено (название, фото, цвет)');
      setTimeout(() => { setSuccess(null); loadData(); }, 2000);
    } catch (err: any) {
      setError(err?.message || 'Не удалось запустить обновление');
    }
  };

  const updateField = <K extends keyof MarkupSettings>(key: K, value: MarkupSettings[K]) => {
    if (settings) setSettings({ ...settings, [key]: value });
  };

  const pagedLogs = logs.slice(logPage * PAGE_SIZE, (logPage + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(logs.length / PAGE_SIZE);

  const formatDuration = (log: SyncLog) => {
    if (!log.finishedAt) return '—';
    const ms = new Date(log.finishedAt).getTime() - new Date(log.startedAt).getTime();
    if (ms < 1000) return `${ms}мс`;
    return `${(ms / 1000).toFixed(1)}с`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Синхронизация и наценки</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Настройки синхронизации с каталогом и engine, формулы наценки
          </p>
        </div>
        <div className="flex items-center gap-3">
          {status && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              status.isRunning
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
            }`}>
              {status.isRunning ? 'Синхронизация...' : 'Ожидание'}
            </span>
          )}
          {status?.isRunning ? (
            <button
              onClick={handleCancelSync}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Отменить
            </button>
          ) : (
            <>
              <button
                onClick={handleRefreshProducts}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Обновить метаданные
              </button>
              <button
                onClick={handleTriggerSync}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Синхронизировать
              </button>
            </>
          )}
        </div>
      </div>

      {/* Прогресс синхронизации */}
      {status?.isRunning && status.progress && status.progress.totalProducts > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {PHASE_LABELS[status.progress.phase] || status.progress.phase}
              </span>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {status.progress.processedProducts} / {status.progress.totalProducts}
            </span>
          </div>
          {/* Прогресс-бар */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.round((status.progress.processedProducts / status.progress.totalProducts) * 100)}%` }}
            ></div>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span className="truncate max-w-xs" title={status.progress.currentArticle}>
              {status.progress.currentArticle}
            </span>
            <div className="flex gap-3 flex-shrink-0">
              <span>Создано: <span className="text-green-600 dark:text-green-400">{status.progress.productsCreated}</span></span>
              <span>Обновлено: <span className="text-blue-600 dark:text-blue-400">{status.progress.productsUpdated}</span></span>
              <span>Размеров: <span className="text-indigo-600 dark:text-indigo-400">{status.progress.sizesUpdated}</span></span>
              {status.progress.errorsCount > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  Ошибок: {status.progress.errorsCount}
                  {' ('}
                  {[
                    status.progress.errCatalogFetch > 0 && `каталог: ${status.progress.errCatalogFetch}`,
                    status.progress.errEngineFetch > 0 && `engine: ${status.progress.errEngineFetch}`,
                    status.progress.errSizeUpsert > 0 && `размеры: ${status.progress.errSizeUpsert}`,
                    status.progress.errProductCreate > 0 && `создание: ${status.progress.errProductCreate}`,
                    status.progress.errInfoLoader > 0 && `info: ${status.progress.errInfoLoader}`,
                  ].filter(Boolean).join(', ')}
                  {')'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Сообщения */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg">{success}</div>
      )}

      {/* Вкладки */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {([['settings', 'Наценки'], ['log', 'Журнал синхронизации']] as [Tab, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tab === key
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Вкладка: Наценки */}
      {tab === 'settings' && settings && (
        <div className="space-y-6">
          {/* Со склада */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Со склада (Каталог)</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Цены из catalog.legitfam.pro. Формула: средняя цена × (1 + %/100) + фикс. надбавка
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Наценка (%)</label>
                <input
                  type="number" step="0.1"
                  value={settings.warehousePercent}
                  onChange={e => updateField('warehousePercent', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Фикс. надбавка (руб.)</label>
                <input
                  type="number" step="100"
                  value={settings.warehouseFixed}
                  onChange={e => updateField('warehouseFixed', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Доставка (дней)</label>
                <input
                  type="number"
                  value={settings.warehouseDeliveryDays}
                  onChange={e => updateField('warehouseDeliveryDays', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Пример: 20 000 × (1 + {settings.warehousePercent}% / 100) + {settings.warehouseFixed} ={' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Math.round(20000 * (1 + settings.warehousePercent / 100) + settings.warehouseFixed).toLocaleString('ru-RU')} руб.
              </span>
            </div>
          </div>

          {/* Из-за границы */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Под заказ (из-за границы)</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Цена берётся из первой доступной площадки по приоритету + наценка
            </p>

            {/* Приоритет площадок */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Приоритет площадок</label>
              <div className="space-y-2">
                {(settings.abroadPricePriority || 'stockx,poizon,goat,sortage,wayoff').split(',').map((platform, idx) => (
                  <div key={platform} className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-5">{idx + 1}.</span>
                    <select
                      value={platform.trim()}
                      onChange={e => {
                        const items = (settings.abroadPricePriority || 'stockx,poizon,goat,sortage,wayoff').split(',').map(s => s.trim());
                        items[idx] = e.target.value;
                        updateField('abroadPricePriority', items.join(','));
                      }}
                      className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      {['stockx', 'poizon', 'goat', 'sortage', 'wayoff'].map(p => (
                        <option key={p} value={p}>{p === 'stockx' ? 'StockX' : p === 'poizon' ? 'Poizon' : p === 'goat' ? 'Goat' : p === 'sortage' ? 'Sortage' : 'Wayoff'}</option>
                      ))}
                    </select>
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          if (idx === 0) return;
                          const items = (settings.abroadPricePriority || 'stockx,poizon,goat,sortage,wayoff').split(',').map(s => s.trim());
                          [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
                          updateField('abroadPricePriority', items.join(','));
                        }}
                        disabled={idx === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"/></svg>
                      </button>
                      <button
                        onClick={() => {
                          const items = (settings.abroadPricePriority || 'stockx,poizon,goat,sortage,wayoff').split(',').map(s => s.trim());
                          if (idx >= items.length - 1) return;
                          [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
                          updateField('abroadPricePriority', items.join(','));
                        }}
                        disabled={idx >= ((settings.abroadPricePriority || 'stockx,poizon,goat,sortage,wayoff').split(',').length - 1)}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">Цена берётся из первой площадки где есть данные. Если нет — следующая по списку.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Наценка (%)</label>
                <input
                  type="number" step="0.1"
                  value={settings.abroadPercent}
                  onChange={e => updateField('abroadPercent', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Фикс. надбавка (руб.)</label>
                <input
                  type="number" step="100"
                  value={settings.abroadFixed}
                  onChange={e => updateField('abroadFixed', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Доставка (дней)</label>
                <input
                  type="number"
                  value={settings.abroadDeliveryDays}
                  onChange={e => updateField('abroadDeliveryDays', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Пример: 15 000 × (1 + {settings.abroadPercent}% / 100) + {settings.abroadFixed} ={' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Math.round(15000 * (1 + settings.abroadPercent / 100) + settings.abroadFixed).toLocaleString('ru-RU')} руб.
              </span>
            </div>
          </div>

          {/* Настройки синхронизации */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Параметры синхронизации</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Интервал (мин.)</label>
                <input
                  type="number"
                  value={settings.syncIntervalMinutes}
                  onChange={e => updateField('syncIntervalMinutes', parseInt(e.target.value) || 60)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Категория по умолчанию (ID)</label>
                <input
                  type="number"
                  value={settings.defaultCategoryId ?? ''}
                  onChange={e => updateField('defaultCategoryId', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Авто"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.autoCreateProducts}
                    onChange={e => updateField('autoCreateProducts', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-500 peer-checked:bg-indigo-600"></div>
                  <span className="ms-3 text-sm font-medium text-gray-700 dark:text-gray-300">Автосоздание товаров</span>
                </label>
              </div>
            </div>
          </div>

          {/* Кнопка сохранения */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}

      {/* Вкладка: Журнал */}
      {tab === 'log' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">История синхронизаций</h2>
            <button onClick={loadData} className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">Обновить</button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Дата', 'Статус', 'Создано', 'Обновлено', 'Размеров', 'Ошибок', 'Время'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pagedLogs.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Нет истории синхронизаций</td></tr>
                )}
                {pagedLogs.map(log => (
                  <React.Fragment key={log.id}>
                    <tr
                      onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                        {new Date(log.startedAt).toLocaleString('ru-RU')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          log.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                          log.status === 'running' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                          log.status === 'cancelled' ? 'bg-gray-100 text-gray-800 dark:bg-gray-600/50 dark:text-gray-300' :
                          'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                        }`}>
                          {log.status === 'success' ? 'Успешно' : log.status === 'running' ? 'Выполняется' : log.status === 'cancelled' ? 'Отменено' : 'Ошибка'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">{log.productsCreated}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">{log.productsUpdated}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">{log.sizesUpdated}</td>
                      <td className="px-4 py-3 text-sm">
                        {log.errorsCount > 0
                          ? <span className="text-red-600 dark:text-red-400 font-medium">{log.errorsCount}</span>
                          : <span className="text-gray-400">0</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatDuration(log)}</td>
                    </tr>
                    {/* Развёрнутые детали */}
                    {expandedLogId === log.id && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30">
                          <div className="space-y-2 text-sm">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Начало:</span>{' '}
                                <span className="text-gray-900 dark:text-gray-200">{new Date(log.startedAt).toLocaleString('ru-RU')}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Окончание:</span>{' '}
                                <span className="text-gray-900 dark:text-gray-200">
                                  {log.finishedAt ? new Date(log.finishedAt).toLocaleString('ru-RU') : '—'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Длительность:</span>{' '}
                                <span className="text-gray-900 dark:text-gray-200">{formatDuration(log)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Итого обработано:</span>{' '}
                                <span className="text-gray-900 dark:text-gray-200">{log.productsCreated + log.productsUpdated} товаров, {log.sizesUpdated} размеров</span>
                              </div>
                            </div>
                            {log.errorMessage && (
                              <div className="mt-2 space-y-2">
                                {log.errorMessage.split(' | ').map((section: string, i: number) => {
                                  const isError = section.startsWith('[ОШИБКИ');
                                  const isNotFound = section.startsWith('[НЕ НАЙДЕНО');
                                  const isDeferred = section.startsWith('[ОТЛОЖЕНО');
                                  const bgClass = isError
                                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                    : isNotFound
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
                                  const textClass = isError
                                    ? 'text-red-700 dark:text-red-300'
                                    : isNotFound
                                    ? 'text-yellow-700 dark:text-yellow-300'
                                    : 'text-blue-700 dark:text-blue-300';
                                  const match = section.match(/^\[([^\]]+)\]\s*(.*)$/);
                                  return (
                                    <div key={i} className={`p-3 rounded border ${bgClass}`}>
                                      <span className={`font-medium ${textClass}`}>{match?.[1] || section}</span>
                                      {match?.[2] && (
                                        <span className={`ml-2 text-sm ${textClass} opacity-80`}>{match[2]}</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Пагинация */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Страница {logPage + 1} из {totalPages} ({logs.length} записей)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setLogPage(p => Math.max(0, p - 1))}
                  disabled={logPage === 0}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Назад
                </button>
                <button
                  onClick={() => setLogPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={logPage >= totalPages - 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Вперёд
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
